import api, { ApiResponse } from './api';

/**
 * 테넌트 통합(흡수) 동의 흐름 클라이언트 (P4-B).
 *
 * 서버 정본: `apps/server/src/companies/company-merge-request.service.ts` +
 * `companies.controller.ts`의 merge-request 라우트.
 *
 * 두 갈래의 경로 축이 다르다:
 *  - 조직 축(`/companies/:id/merge-requests`) — OrganizationGuard가 붙어 `companyId` 쿼리 필수.
 *  - 토큰 축(`/companies/merge-requests/:token/...`) — 조직 식별자가 없어 가드는 통과(pass-through)하고
 *    서버의 OWNER/멤버십 검증이 최종 방어선이다. 클라이언트가 조직을 붙일 자리가 아니다.
 */

export type CompanyMergeRequestStatus =
  | 'PENDING'
  | 'ACCEPTED'
  | 'EXECUTED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'EXPIRED';

/** 목록 응답 1건 — 서버가 canonicalCompany/targetCompany를 include로 붙여 내려준다. */
export interface CompanyMergeRequest {
  id: string;
  token: string;
  status: CompanyMergeRequestStatus;
  canonicalCompanyId: string;
  targetCompanyId: string;
  requestedByUserId: string;
  rejectReason: string | null;
  expiresAt: string;
  resolvedAt: string | null;
  executedAt: string | null;
  createdAt: string;
  canonicalCompany?: { id: string; name: string };
  targetCompany?: { id: string; name: string };
}

/** 수락 화면(대상 조직 OWNER)용 단건 조회 — 서버 MergeRequestInfo와 1:1. */
export interface CompanyMergeRequestInfo {
  id: string;
  token: string;
  status: CompanyMergeRequestStatus;
  canonicalCompanyName: string;
  targetCompanyName: string;
  requesterName: string | null;
  /** 수락 시 함께 이동하는 규모 — 동의 화면에 그대로 보여준다. */
  targetGroupCount: number;
  targetMemberCount: number;
  targetSiteLinkCount: number;
  expiresAt: string;
  createdAt: string;
}

export interface AcceptCompanyMergeResult {
  id: string;
  status: CompanyMergeRequestStatus;
  /** 우리 소속이 옮겨간 정본 조직 — 수락 직후 이 조직으로 전환해야 한다. */
  canonicalCompanyId: string;
  /**
   * 수락 직후 흡수 엔진까지 성공했는지. false면 ACCEPTED로 남아 운영자 중재(재시도) 대상이므로
   * "완료"가 아니라 "접수됨"으로 안내해야 한다.
   */
  executed: boolean;
  executionSummary: Record<string, unknown> | null;
}

export const companyMergeApi = {
  /** 조직의 통합 요청 목록 (요청측·대상측 모두) — 자기 조직 OWNER만. */
  list: async (companyId: string): Promise<CompanyMergeRequest[]> => {
    const response = await api.get<ApiResponse<CompanyMergeRequest[]>>(
      `/companies/${companyId}/merge-requests?companyId=${companyId}`,
    );
    return response.data.data ?? [];
  },

  /** 통합 요청 생성 — 클레임 정본 조직 OWNER만. companyId = 정본(자기 조직) id. */
  create: async (companyId: string, targetCompanyId: string): Promise<CompanyMergeRequest> => {
    const response = await api.post<ApiResponse<CompanyMergeRequest>>(
      `/companies/${companyId}/merge-requests?companyId=${companyId}`,
      { targetCompanyId },
    );
    return response.data.data!;
  },

  /** 요청 취소 — 요청 조직 OWNER, PENDING에서만. 토큰이 아니라 요청 id를 쓴다. */
  cancel: async (requestId: string): Promise<void> => {
    await api.post<ApiResponse<{ cancelled: boolean }>>(
      `/companies/merge-requests/${requestId}/cancel`,
      {},
    );
  },

  /** 수락 화면용 단건 조회 — 대상 조직 활성 멤버만(PENDING·미만료 건만 열린다). */
  getByToken: async (token: string): Promise<CompanyMergeRequestInfo> => {
    const response = await api.get<ApiResponse<CompanyMergeRequestInfo>>(
      `/companies/merge-requests/${token}`,
    );
    return response.data.data!;
  },

  /** 수락 — 대상 조직 OWNER만. 성공 시 우리 조직이 소멸하므로 호출 측이 세션을 다시 읽어야 한다. */
  accept: async (token: string): Promise<AcceptCompanyMergeResult> => {
    const response = await api.post<ApiResponse<AcceptCompanyMergeResult>>(
      `/companies/merge-requests/${token}/accept`,
      {},
    );
    return response.data.data!;
  },

  /** 거절 — 대상 조직 OWNER만. 사유는 운영자 중재 목록에 표시된다. */
  reject: async (token: string, reason?: string): Promise<void> => {
    await api.post<ApiResponse<{ rejected: boolean }>>(
      `/companies/merge-requests/${token}/reject`,
      reason ? { reason } : {},
    );
  },
};
